/**
 * Feeds the signed-in tenancy's customer feature flags onto the app's flag store.
 *
 * ## Adding and reading a flag
 *
 * 1. **Declare it twice, identically** — `commons/features/__init__.py`
 *    (`FEATURE_FLAGS`) and `@authe/fe-commons/features` (`FEATURE_FLAGS`), with
 *    its default, kind and owner. `featuresParity.test.ts` fails if they drift;
 *    a name read anywhere must be declared.
 * 2. **Read it** with `useFeatureFlag("my_flag")` (or `<Flagged name="my_flag">`)
 *    from `@authe/ui-core/flags`. Nothing else: not this component, not the
 *    source, not the response.
 * 3. **Set it** per customer / org / tenant / user in the admin portal (a
 *    client-config `features` row; internal-only to write). The most specific
 *    rung wins; unset rungs inherit; nothing set = the declared default.
 *
 * A feature flag answers "is this rolled out to them?". "Did they pay for it?"
 * is a plan entitlement (`useEntitled`), and "may this person do it?" is a scope
 * (`usePermissions`) — gate on those, not on a flag.
 *
 * ## How the layer is fed
 *
 * One read of management-api `GET /features` per tenancy — react-query, keyed by
 * the full customer/org/tenant/user identity, so a tenant or org switch reads
 * afresh — then again every {@link FEATURE_FLAGS_FRESH_MS} while the tab is
 * visible, and on focus once that long has passed. It used to be read once per
 * session, which made every flag a switch that only worked FORWARDS: turning a
 * broken feature off reached nobody until they reloaded. Its answer is
 * pushed through `featureFlagLayer` (declared flags only, defaults filled) onto
 * `FeatureFlags.setClaims` — the layer the JWT `feature_flags` claim used to
 * occupy, which nothing ever wrote. Precedence is unchanged:
 * `override > policy > claim (these flags) > default`, so a `?debug=1` override
 * still wins for support.
 *
 * Fails to the DEFAULTS, never to off: while the read is in flight, after it
 * fails, and for a declared flag the response omits, the layer holds each flag's
 * declared default. A transient refetch failure keeps the tenancy's last good
 * answer rather than flipping every flag back.
 *
 * Renders nothing. Mounted inside `<FlagsProvider>` (it writes that store) and
 * only when a session exists (the read needs a tenancy).
 */
import * as React from "react";
import { useFeatureFlags } from "@authe/ui-core/flags";
import {
  FEATURE_FLAGS,
  featureFlagLayer,
  type FeatureFlagDefinition,
} from "@authe/fe-commons/features";
import { useTenancyFeatureFlags } from "./tenancyFeatureFlags";

export interface DashboardFeatureFlagsProps {
  /** The declared flags. Defaults to the shared registry; tests pass their own. */
  registry?: readonly FeatureFlagDefinition[];
}

/**
 * How long a tenancy's flags are trusted. A flag is also a kill switch, so an
 * operator's change has to reach the sessions already open — within minutes,
 * not at their next sign-in. One small GET per visible tab per interval.
 */
export const FEATURE_FLAGS_FRESH_MS = 60_000;

export function DashboardFeatureFlags({
  registry = FEATURE_FLAGS,
}: DashboardFeatureFlagsProps): React.ReactElement | null {
  const flags = useFeatureFlags();
  const { data, isError } = useTenancyFeatureFlags();

  // `data` is undefined while loading and after a failed first read, which
  // `featureFlagLayer` answers with every declared default.
  const layer = React.useMemo(() => featureFlagLayer(data ?? null, registry), [data, registry]);

  React.useEffect(() => {
    flags.setClaims(layer);
  }, [flags, layer]);

  return isError ? <p role="status" className="px-4 py-2 text-sm text-muted-foreground">Workspace configuration is temporarily unavailable. {data ? "Showing the last loaded features." : "Showing default features."}</p> : null;
}
